/* eslint-disable no-unused-vars */
import { useState, useEffect, useId } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from 'services/newPlatformAuth.service';
import { Loader, CustomInput, Buttons } from 'components';
import { Eye, EyeOff, ArrowUp, CloseForm, CheckForm } from 'assets/icons';
import { showPolipop } from 'utils';
import * as S from './ResetPassword.styled';

const passValidation = [
  { title: 'At least one upper case english letter', pattern: '^(?=.*[A-Z])' },
  { title: 'At least one lower case english letter', pattern: '^(?=.*[a-z])' },
  { title: 'At least one digit', pattern: '^(?=.*[0-9])' },
  { title: 'At least one special character', pattern: '[^A-Za-z0-9]' },
  { title: 'Minimum 8 in length', pattern: '^.{8,}$' },
];

const validatePassword = (value) => {
  for (const { title, pattern } of passValidation) {
    const regex = new RegExp(pattern);
    if (!regex.test(value)) return title;
  }
  return true;
};

export const ResetPassword = () => {
  const {
    handleSubmit,
    getValues,
    control,
    trigger,
    clearErrors,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
  } = useForm({
    mode: 'onChange',
    shouldFocusError: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guid = queryParams.get('guid');
  const formId = useId();
  const passId = useId();
  const passConfirmId = useId();
  const [userChangeForgotenPassword, result] = authAPI.useChangeForgotenPasswordMutation();
  const [userValidateForgotPassword, validationResult] =
    authAPI.useValidateForgotPasswordMutation();

  useEffect(() => {
    userValidateForgotPassword({ guid });
  }, []);

  useEffect(() => {
    if (validationResult.isError) {
      navigate('/');
    }
  }, [validationResult.isError]);

  useEffect(() => {
    if (result.isError) {
      showPolipop(result);
    }
  }, [result]);

  const onSubmit = (data) => {
    const dataToSend = {
      guid,
      password: data.password,
    };
    userChangeForgotenPassword(dataToSend);
  };

  const handleBlur = (field) => {
    trigger(field);
  };

  if (validationResult.isLoading || validationResult.status === 'uninitialized')
    return (
      <S.Box>
        <Loader.Local delay={100} />
      </S.Box>
    );

  if (
    isSubmitSuccessful &&
    !result.isError &&
    !result.isLoading &&
    result.status !== 'uninitialized'
  )
    return (
      <S.Box>
        <S.SubmitSuccessfulBox>
          <S.Title>Reset Password</S.Title>
          <S.Paragraph>Password reset successfully!</S.Paragraph>
          <Buttons.PrimaryNormal
            type='submit'
            style={{ width: '100%' }}
            isDisabled={!isValid || isSubmitting || result.isLoading}
            onClick={() => navigate('/userLogin')}
          >
            Login
          </Buttons.PrimaryNormal>
        </S.SubmitSuccessfulBox>
      </S.Box>
    );

  if (!validationResult.isError && validationResult.isSuccess)
    return (
      <S.Box>
        {(isSubmitting || result.isLoading) && (
          <S.LoaderBox>
            <Loader.Local delay={100} />
          </S.LoaderBox>
        )}
        <S.StyledForm id={formId} onSubmit={handleSubmit(onSubmit)}>
          <S.Title>Reset Password</S.Title>
          <S.Paragraph>
            Choose a new password and confirm it. You can then sign in to your account using your
            new password.
          </S.Paragraph>
          <Controller
            name='password'
            control={control}
            rules={{
              required: 'Password is required',
              validate: validatePassword,
            }}
            render={(props) => {
              const { field, formState } = props;
              const errors = formState.errors;
              const [showPassword, setShowPassword] = useState(false);
              return (
                <div style={{ marginBottom: '24px' }}>
                  <CustomInput
                    type={showPassword ? 'text' : 'password'}
                    name={field.name}
                    onFocus={() => {
                      clearErrors('password');
                    }}
                    onBlur={() => handleBlur('password')}
                    onChange={field.onChange}
                    error={Boolean(errors.password)}
                    value={field.value || ''}
                    rightIcon={showPassword ? <EyeOff /> : <Eye />}
                    rightIconOnClick={() => setShowPassword((prev) => !prev)}
                    id={passId}
                    topLabel='New password:'
                    containerStyle={{ height: '74px' }}
                    inputStyle={errors.password ? { border: '1px solid #FCA5A5 !important' } : {}}
                  />
                  <S.PassValidationBox>
                    {passValidation.map((item) => {
                      const pattern = new RegExp(item.pattern);
                      const isNotEmpty = field.value?.length > 0;
                      const isComplete = pattern.test(field.value);
                      return (
                        <S.ValidationItem
                          isRed={isNotEmpty && !isComplete}
                          isGreen={isNotEmpty && isComplete}
                          key={item.title}
                        >
                          {item.title}
                        </S.ValidationItem>
                      );
                    })}
                  </S.PassValidationBox>
                </div>
              );
            }}
          />
          <Controller
            name='passwordConfirm'
            control={control}
            rules={{
              required: 'Confirm password field does not match new password',
              validate: (value) => {
                const { password } = getValues();
                return value === password || 'Confirm password field does not match new password';
              },
            }}
            render={(props) => {
              const { field, formState } = props;
              const errors = formState.errors;
              const [showPassword, setShowPassword] = useState(false);
              return (
                <S.InputWithCustomErrorWrap>
                  <CustomInput
                    {...props.field}
                    type={showPassword ? 'text' : 'password'}
                    name={field.name}
                    onFocus={() => {
                      clearErrors('passwordConfirm');
                    }}
                    onBlur={() => handleBlur('passwordConfirm')}
                    onChange={field.onChange}
                    error={Boolean(errors.passwordConfirm)}
                    value={field.value || ''}
                    rightIcon={showPassword ? <EyeOff /> : <Eye />}
                    rightIconOnClick={() => setShowPassword((prev) => !prev)}
                    id={passConfirmId}
                    topLabel='Confirm new password:'
                    containerStyle={{ height: '74px' }}
                    inputStyle={
                      errors.passwordConfirm ? { border: '1px solid #FCA5A5 !important' } : {}
                    }
                  />
                  {errors?.passwordConfirm?.message && (
                    <S.ValidationError>{errors?.passwordConfirm?.message}</S.ValidationError>
                  )}
                </S.InputWithCustomErrorWrap>
              );
            }}
          />
          <Buttons.PrimaryNormal
            type='submit'
            style={{ width: '100%' }}
            isDisabled={!isValid || isSubmitting || result.isLoading}
          >
            Reset password
          </Buttons.PrimaryNormal>
        </S.StyledForm>
      </S.Box>
    );
};
